/**
 * interfaces
 */
import { I18nObject, IAppValidationErrors } from "../interfaces";

/**
 * validators
 */
import { AppValidators } from "../validators";

/**
 * Strongly-typed container to build Angular ValidationErrors with optional i18n payload.
 *
 * This model standardizes the error shape produced by validators so UI layers can reliably
 * extract both the error identifier and the translated message configuration.
 *
 * @typeParam TErrorValue - The type of the error value (defaults to boolean). Use a specific
 * type when your validator needs to return metadata (e.g. `{ requiredLength: number }`).
 *
 * @example
 * // Producing a minlength error with i18n configuration
 * const model = new AppValidationErrorsModel(
 *     'minlength',
 *     { requiredLength: 8, actualLength: 4 },
 *     { key: 'FORM.VALIDATION.MIN_LENGTH', params: { length: 8 } }
 * );
 * const errors = model.errors;
 * // => {
 * //      i18n: { key: 'FORM.VALIDATION.MIN_LENGTH', params: { length: 8 } },
 * //      errorName: 'minlength',
 * //      errorValue: { requiredLength: 8, actualLength: 4 }
 * //    }
 *
 * @example
 * // Client usage: retrieving error from form control
 * const passwordControl = this.form.get('password');
 * const errors = passwordControl?.errors;
 * if (errors?.['minlength']) {
 *     const error = errors['minlength'];
 *     const i18n = errors['i18n'];
 *     // Display translated message using i18n.key and i18n.params
 *     console.log('Error:', error.errorName); // 'minlength'
 *     console.log('Required length:', error.errorValue.requiredLength); // 8
 *     console.log('Translation key:', i18n?.key); // 'FORM.VALIDATION.MIN_LENGTH'
 * }
 */
export class AppValidationErrorsModel<TErrorValue = boolean> {
    
    /**
     * Constructor
     * @param {string} errorName - Stable identifier of the validation error (e.g. `required`, `minlength`).
     * @param {TErrorValue} errorValue - Payload of the error. For simple flags use `true`; for rich
     * values provide an object (e.g. `{ requiredLength: number, actualLength: number }`).
     * @param {I18nObject} [i18n] - Optional translation configuration used by the UI layer to render messages.
     */
    constructor(
        public readonly errorName: string,
        public readonly errorValue: TErrorValue,
        public readonly i18n?: I18nObject
    ) {}

    /**
     * Builds a plain object consumable as Angular `ValidationErrors`.
     *
     * Structure:
     * - `errorName`: string
     * - `errorValue`: TErrorValue
     * - `i18n?`: I18nObject
     *
     * @returns {IAppValidationErrors<TErrorValue>} Validation errors object with optional i18n payload
     *
     * @example
     * new AppValidationErrorsModel('required', true, { key: 'FORM.VALIDATION.REQUIRED' }).errors;
     */
    public get errors(): IAppValidationErrors<TErrorValue> {

        // initialize the error object
        let _errorObject: IAppValidationErrors<TErrorValue> = {} as IAppValidationErrors<TErrorValue>;
        
        // add the i18n object to the error object
        if (typeof this.i18n === 'object') {
            
            // ES6 destructuring
            const { key, params } = this.i18n;

            _errorObject['i18n'] = AppValidators.i18nFn(key, params).i18n;

        }

        // add the error name and value to the error object
        _errorObject['errorName'] = this.errorName;
        _errorObject['errorValue'] = this.errorValue;

        return _errorObject;
    }

}
